import { type Stripe, loadStripe } from '@stripe/stripe-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CustomerApiError, customerApi } from '../customer_api';
import { getStripePublishableKey } from '../lib/stripe_env';

/**
 * AddCardPanel — Stripe.js SetupIntent flow for saving a card without a
 * payment (FIX 11.5).
 *
 * Flow:
 *   1. beginSetup: POST /api/customer/payment-methods/setup-intent
 *      → { client_secret, setup_intent_id, stripe_account_id }
 *   2. loadStripe(publishableKey, { stripeAccount }) + mount PaymentElement
 *      (mode 'setup', currency implied by SetupIntent).
 *   3. confirmSetup: stripe.confirmSetup with redirect:'if_required'.
 *   4. onSaved() on success; inline neutral-style error on failure.
 *   5. onCancel dismisses the panel without saving.
 */

interface SetupIntentResponse {
  client_secret: string;
  setup_intent_id: string;
  stripe_account_id: string | null;
}

type Phase = 'idle' | 'creating_intent' | 'element_ready' | 'submitting' | 'saved' | 'error';

interface Props {
  onSaved: () => void;
  onCancel: () => void;
}

export function AddCardPanel({ onSaved, onCancel }: Props) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [intentData, setIntentData] = useState<SetupIntentResponse | null>(null);

  const stripeRef = useRef<Stripe | null>(null);
  // biome-ignore lint/suspicious/noExplicitAny: StripeElements isn't worth re-typing here.
  const elementsRef = useRef<any>(null);
  const mountNodeRef = useRef<HTMLDivElement | null>(null);

  const fail = useCallback((msg: string) => {
    setPhase('error');
    setErrorMsg(msg);
  }, []);

  // Step 1+2+3: create SetupIntent + load Stripe + mount Element.
  const beginSetup = useCallback(async () => {
    if (phase === 'creating_intent' || phase === 'submitting') return;
    setPhase('creating_intent');
    setErrorMsg(null);

    let data: SetupIntentResponse;
    try {
      data = await customerApi<SetupIntentResponse>('/api/customer/payment-methods/setup-intent', {
        method: 'POST',
      });
    } catch (err) {
      const msg =
        err instanceof CustomerApiError
          ? `Could not start card setup (HTTP ${err.status}).`
          : 'Could not start card setup. Please try again.';
      fail(msg);
      return;
    }

    setIntentData(data);

    const pk = getStripePublishableKey();
    if (!pk) {
      fail('Payments not available at this time. Please try again later.');
      return;
    }

    let stripe: Stripe | null;
    try {
      stripe = await loadStripe(
        pk,
        data.stripe_account_id ? { stripeAccount: data.stripe_account_id } : undefined,
      );
    } catch {
      fail('Could not load Stripe. Please refresh and try again.');
      return;
    }
    if (!stripe) {
      fail('Stripe failed to initialise. Please refresh and try again.');
      return;
    }
    stripeRef.current = stripe;
    setPhase('element_ready');
  }, [phase, fail]);

  // Mount the PaymentElement once the target div is rendered.
  useEffect(() => {
    if (phase !== 'element_ready') return;
    if (!intentData || !stripeRef.current || !mountNodeRef.current) return;

    const elements = stripeRef.current.elements({
      clientSecret: intentData.client_secret,
      appearance: { theme: 'stripe' },
    });
    elementsRef.current = elements;
    const paymentElement = elements.create('payment');
    paymentElement.mount(mountNodeRef.current);

    return () => {
      try {
        paymentElement.unmount();
      } catch {
        // ignore — happens if the element was already torn down.
      }
    };
  }, [intentData, phase]);

  // Step 4: confirmSetup.
  const submitSetup = useCallback(async () => {
    if (!stripeRef.current || !elementsRef.current) {
      fail('Card entry not ready. Please try again.');
      return;
    }
    setPhase('submitting');
    setErrorMsg(null);

    const { error } = await stripeRef.current.confirmSetup({
      elements: elementsRef.current,
      redirect: 'if_required',
      confirmParams: {
        return_url: `${window.location.origin}/account/payment-methods`,
      },
    });

    if (error) {
      fail(error.message ?? 'Could not save card. Please try again.');
      return;
    }

    setPhase('saved');
    onSaved();
  }, [fail, onSaved]);

  // Auto-begin on first render so the panel opens into the loading state
  // rather than an extra "open" button click.
  useEffect(() => {
    if (phase === 'idle') {
      beginSetup();
    }
  }, []); // run once on mount

  if (phase === 'saved') return null;

  return (
    <div
      style={{
        marginTop: '1rem',
        padding: '1.25rem',
        background: 'var(--surface, white)',
        borderRadius: '12px',
        border: '1px solid var(--border, oklch(0.915 0.005 80))',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '0.75rem',
        }}
      >
        <div
          style={{
            fontSize: '0.7rem',
            textTransform: 'uppercase' as const,
            letterSpacing: '0.14em',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          }}
        >
          Add a card
        </div>
        {phase !== 'submitting' && (
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '0.25rem 0.5rem',
              borderRadius: '6px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              background: 'transparent',
              fontSize: '0.78rem',
              cursor: 'pointer',
              color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
            }}
          >
            Cancel
          </button>
        )}
      </div>

      {phase === 'creating_intent' && (
        <p
          style={{
            margin: 0,
            fontSize: '0.9rem',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          }}
        >
          Preparing card entry…
        </p>
      )}

      {(phase === 'element_ready' || phase === 'submitting') && (
        <>
          <div ref={mountNodeRef} data-testid="add-card-element" />
          <button
            type="button"
            onClick={submitSetup}
            disabled={phase === 'submitting'}
            data-testid="add-card-confirm"
            style={{
              display: 'block',
              width: '100%',
              marginTop: '1rem',
              padding: '0.85rem 1rem',
              borderRadius: '10px',
              border: 'none',
              background: 'var(--primary, oklch(0.43 0.18 263))',
              color: 'white',
              fontSize: '0.95rem',
              fontWeight: 500,
              cursor: phase === 'submitting' ? 'not-allowed' : 'pointer',
              opacity: phase === 'submitting' ? 0.6 : 1,
            }}
          >
            {phase === 'submitting' ? 'Saving…' : 'Save card'}
          </button>
        </>
      )}

      {phase === 'error' && (
        <>
          <p
            style={{
              margin: '0 0 0.75rem',
              fontSize: '0.9rem',
              color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
            }}
          >
            {errorMsg ?? 'Could not save card.'}
          </p>
          <button
            type="button"
            onClick={beginSetup}
            style={{
              display: 'block',
              width: '100%',
              padding: '0.75rem 1rem',
              borderRadius: '10px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              background: 'var(--surface, white)',
              color: 'var(--foreground, oklch(0.19 0.012 75))',
              fontSize: '0.9rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </>
      )}
    </div>
  );
}
